import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { AuditCase } from '../audit.models';
import { AuditService } from '../audit.service';
import { auditNavItems, formatAuditDate, statusSeverity } from '../audit-ui';

@Component({
  selector: 'app-audit-cases',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ButtonModule,
    ProgressBarModule,
    TableModule,
    TagModule,
    ToastModule,
    TooltipModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService],
  template: `
    <p-toast></p-toast>
    @if (loading) {
      <p-progressBar mode="indeterminate"></p-progressBar>
    }

    <app-page-shell>
      <app-page-hero eyebrow="Audit Execution" title="Audit Cases" subtitle="Manual audit cases across LogiLink tenant modules.">
        <div nav>
          @for (item of visibleNavItems; track item.route) {
            <a pButton [routerLink]="item.route" [label]="item.label" [icon]="item.icon" [outlined]="item.route !== '/audit/cases'" severity="secondary"></a>
          }
        </div>
        <div actions>
          <p-button label="Refresh" icon="pi pi-refresh" [outlined]="true" severity="secondary" (onClick)="load()"></p-button>
          <a pButton routerLink="/audit/cases/create" label="New Case" icon="pi pi-plus" [class.p-disabled]="!canCreate"></a>
        </div>
      </app-page-hero>

      <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

      <app-page-data-panel title="Cases" subtitle="Lifecycle, score, source reference, and due-date tracking." [resultText]="cases.length + ' cases'">
        <p-table [value]="cases" [loading]="loading" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '1100px' }">
          <ng-template pTemplate="header">
            <tr>
              <th>Case</th>
              <th>Module</th>
              <th>Scope</th>
              <th>Source</th>
              <th>Score</th>
              <th>Status</th>
              <th>Due</th>
              <th class="text-right">Actions</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-auditCase>
            <tr>
              <td>
                <div class="audit-stack">
                  <strong>{{ auditCase.auditNumber }}</strong>
                  <small>{{ auditCase.auditType || 'Manual audit' }}</small>
                </div>
              </td>
              <td>{{ auditCase.moduleCode || '-' }}</td>
              <td>
                <div class="audit-stack">
                  <span>{{ auditCase.scopeDisplayName || auditCase.scopeNumber || auditCase.scopeType || '-' }}</span>
                  <small>{{ auditCase.scopeType || '-' }}</small>
                </div>
              </td>
              <td>
                <div class="audit-stack">
                  <span>{{ auditCase.sourceDisplayName || auditCase.sourceResourceNumber || '-' }}</span>
                  <small>{{ auditCase.sourceModule || auditCase.sourceResourceType || '-' }}</small>
                </div>
              </td>
              <td>{{ auditCase.score ?? 0 }} / {{ auditCase.maxScore ?? 0 }}</td>
              <td><p-tag [value]="auditCase.status || '-'" [severity]="statusSeverity(auditCase.status)"></p-tag></td>
              <td>{{ formatDate(auditCase.dueAt) }}</td>
              <td class="text-right">
                <a pButton [routerLink]="['/audit/cases', auditCase.auditCaseId]" icon="pi pi-arrow-right" [text]="true" pTooltip="Open"></a>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="8" class="text-center p-4">No audit cases are available.</td></tr>
          </ng-template>
        </p-table>
      </app-page-data-panel>
    </app-page-shell>
  `,
  styles: [`
    .audit-stack {
      display: flex;
      flex-direction: column;
      gap: .25rem;
      min-width: 0;
    }

    .audit-stack small {
      color: #64748b;
      font-size: .78rem;
    }
  `]
})
export class AuditCasesComponent implements OnInit {
  private readonly auditService = inject(AuditService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  loading = false;
  cases: AuditCase[] = [];

  readonly visibleNavItems = auditNavItems.filter(item => this.permissionService.hasPermission(item.permission));
  readonly canCreate = this.permissionService.hasPermission(app_permissions.audit.cases.create);
  readonly statusSeverity = statusSeverity;
  readonly formatDate = formatAuditDate;

  ngOnInit(): void {
    this.load();
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Cases', value: this.cases.length, tone: 'info' },
      { label: 'In Progress', value: this.cases.filter(item => item.status === 'IN_PROGRESS').length, tone: 'warn' },
      { label: 'Submitted', value: this.cases.filter(item => item.status === 'SUBMITTED').length },
      { label: 'Closed', value: this.cases.filter(item => item.status === 'CLOSED').length, tone: 'success' }
    ];
  }

  load(): void {
    this.loading = true;
    this.auditService.cases().subscribe({
      next: response => this.cases = response.data ?? [],
      error: () => this.messageService.add({ severity: 'error', summary: 'Cases', detail: 'Unable to load audit cases.' }),
      complete: () => this.loading = false
    });
  }
}
